import { useCallback, useEffect, useState } from "react";
import { api, playerUrl, type Playlist, type Screen } from "../api";
import { durationLabel, relativeTime } from "../format";
import { Banner, Dialog, EmptyState, FilterChips, PageHeader, SearchField, StatusPill } from "../ui";
import { DeviceRow } from "./Authorizations";

type ScreenFilter = "all" | "online" | "waiting" | "offline";

export function ScreensPage({ path, navigate }: { path: string; navigate: (to: string) => void }) {
  const screenId = path.startsWith("/ecrans/") ? decodeURIComponent(path.slice("/ecrans/".length)) : "";
  const [screens, setScreens] = useState<Screen[] | null>(null);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<ScreenFilter>(readScreenFilter);

  async function load() {
    const [screenData, playlistData] = await Promise.all([
      api<{ screens: Screen[] }>("/api/screens"),
      api<{ playlists: Playlist[] }>("/api/playlists"),
    ]);
    setScreens(screenData.screens);
    setPlaylists(playlistData.playlists);
  }

  useEffect(() => {
    load().catch((reason: unknown) => setError(message(reason)));
    const timer = window.setInterval(() => {
      load().catch(() => undefined);
    }, 30000);
    return () => window.clearInterval(timer);
  }, []);

  const selected = screens?.find((screen) => screen.id === screenId) ?? null;
  const needle = query.trim().toLowerCase();
  const visible = (screens ?? []).filter((screen) => {
    if (filter !== "all" && screen.status !== filter) return false;
    if (!needle) return true;
    const haystack = [screen.name, screen.location ?? "", screen.assignment?.playlistName ?? "", screen.playbackIp ?? "", screen.id]
      .join(" ")
      .toLowerCase();
    return haystack.includes(needle);
  });

  return (
    <section>
      {!screenId ? (
        <PageHeader
          kicker="Diffusion"
          title="Écrans"
          lede="Chaque télé a un lien, une playlist, et un code à valider avant la première image."
          action={<CreateScreen onCreated={(screen) => { setNotice(`Écran ${screen.name} créé.`); void load(); navigate(`/ecrans/${screen.id}`); }} />}
        />
      ) : null}
      {error ? <Banner tone="error">{error}</Banner> : null}
      {notice ? <Banner tone="ok">{notice}</Banner> : null}
      {screenId && !screens ? <p className="muted">Chargement de l'écran…</p> : null}
      {screenId && selected ? (
        <ScreenDetail
          screen={selected}
          playlists={playlists}
          onBack={() => navigate("/ecrans")}
          onChanged={async (text) => {
            setNotice(text);
            await load();
          }}
          onDeleted={() => {
            setNotice("Écran supprimé.");
            navigate("/ecrans");
            void load();
          }}
        />
      ) : null}
      {screenId && screens && !selected ? <Banner tone="error">Écran introuvable.</Banner> : null}
      {!screenId && screens ? <DroppedCallout screens={screens} navigate={navigate} /> : null}
      {!screenId && screens ? <PendingCallout screens={screens} navigate={navigate} /> : null}
      {!screenId && screens && screens.length > 0 ? (
        <div className="toolbar">
          <SearchField value={query} onChange={setQuery} placeholder="Nom, emplacement, playlist" />
          <FilterChips
            label="Filtrer les écrans"
            value={filter}
            onChange={setFilter}
            options={[
              { id: "all", label: "Tous" },
              { id: "online", label: "En lecture" },
              { id: "waiting", label: "En attente" },
              { id: "offline", label: "Hors ligne" },
            ]}
          />
        </div>
      ) : null}
      {!screenId && screens && screens.length === 0 ? (
        <EmptyState
          icon="screen"
          title="Aucune télé pour le moment"
          text="Créez le premier écran, par exemple Cardio. Vous obtiendrez un lien à coller dans la box."
        />
      ) : null}
      {!screenId && screens && screens.length > 0 && visible.length === 0 ? (
        <p className="quiet-empty">Aucun écran pour cette recherche.</p>
      ) : null}
      {!screenId ? (
        <div className="screen-grid">
          {visible.map((screen) => {
            const ip = ipOf(screen);
            return (
              <article key={screen.id} className={`screen-card ${screen.dropped ? "dropped" : screen.status}`}>
                <button type="button" className="screen-card-main" onClick={() => navigate(`/ecrans/${screen.id}`)}>
                  <span className="screen-card-top">
                    <StatusPill status={screen.status} />
                    {screen.dropped ? <span className="pill alert"><span className="dot" />Silence</span> : null}
                    {screen.status !== "waiting" && screen.devices.some((device) => device.status === "pending") ? (
                      <span className="pill waiting"><span className="dot" />Code</span>
                    ) : null}
                  </span>
                  <h2>{screen.name}</h2>
                  <p className="muted">{screen.location || "Emplacement non renseigné"}</p>
                  <p className={screen.assignment ? "screen-play" : "screen-play missing"}>
                    {screen.assignment ? screen.assignment.playlistName : "Assigner une playlist"}
                  </p>
                  <p className="meta">{relativeTime(screen.lastSeen)} · {ip.label} {ip.value}</p>
                </button>
                <div className="screen-card-actions">
                  <button type="button" onClick={() => navigate(`/ecrans/${screen.id}`)}>Ouvrir</button>
                  <button type="button" onClick={() => void refresh(screen.id, setNotice, setError)}>Relancer</button>
                </div>
              </article>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

function ScreenDetail({
  screen,
  playlists,
  onBack,
  onChanged,
  onDeleted,
}: {
  screen: Screen;
  playlists: Playlist[];
  onBack: () => void;
  onChanged: (text: string) => Promise<void>;
  onDeleted: () => void;
}) {
  const [name, setName] = useState(screen.name);
  const [location, setLocation] = useState(screen.location ?? "");
  const [audioEnabled, setAudioEnabled] = useState(screen.audioEnabled);
  const [playlistId, setPlaylistId] = useState(screen.assignment?.playlistId ?? "");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const url = playerUrl(screen);
  const ip = ipOf(screen);
  const dirty =
    name !== screen.name ||
    location !== (screen.location ?? "") ||
    audioEnabled !== screen.audioEnabled ||
    playlistId !== (screen.assignment?.playlistId ?? "");
  const approved = screen.devices.some((device) => device.status === "approved");

  useEffect(() => {
    setName(screen.name);
    setLocation(screen.location ?? "");
    setAudioEnabled(screen.audioEnabled);
    setPlaylistId(screen.assignment?.playlistId ?? "");
    // Ne pas réinitialiser le formulaire à chaque synchro : la liste se
    // rafraîchit toute seule et écraserait une saisie en cours.
  }, [screen.id]);

  useEffect(() => {
    if (!dirty) return;
    const onLeave = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [dirty]);

  function back() {
    if (dirty && !window.confirm("Quitter sans enregistrer les réglages ?")) return;
    onBack();
  }

  async function save() {
    setError("");
    try {
      await api(`/api/screens/${screen.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          name,
          location,
          audioEnabled,
          playlistId: playlistId || null,
        }),
      });
      await onChanged("Écran enregistré. La télé prend le changement à la fin du média en cours.");
    } catch (reason) {
      setError(message(reason));
    }
  }

  async function remove() {
    if (!window.confirm(`Supprimer l'écran ${screen.name} ? Le lien lecteur ne fonctionnera plus.`)) return;
    try {
      await api(`/api/screens/${screen.id}`, { method: "DELETE" });
      onDeleted();
    } catch (reason) {
      setError(message(reason));
    }
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Le lien n'a pas pu être copié. Sélectionnez-le à la main.");
    }
  }

  return (
    <div className="stack">
      <header className="detail-head">
        <button type="button" className="text-button back" onClick={back}>Tous les écrans</button>
        <div className="detail-title">
          <div>
            <h1>{screen.name}</h1>
            <p className="muted">{screen.location || "Emplacement non renseigné"} · vu {relativeTime(screen.lastSeen)}</p>
          </div>
          <StatusPill status={screen.status} />
        </div>
      </header>
      {screen.dropped ? <p className="callout alert">Cette télé ne répond plus. Vérifiez qu'elle est allumée, ou relancez-la.</p> : null}
      {!approved ? (
        <p className="callout">Aucune télé autorisée pour cet écran. Quand le code s'affiche, autorisez l'appareil plus bas.</p>
      ) : null}
      {error ? <Banner tone="error">{error}</Banner> : null}
      <div className="detail-grid">
        <article className="card link-card">
          <h2>Lien de la télé</h2>
          <p className="muted">À coller dans Vitrine Signage Player, en plein écran, au démarrage de la box.</p>
          <label>
            Adresse
            <span className="copy-box">
              <input readOnly value={url} onFocus={(event) => event.currentTarget.select()} />
              <button type="button" className="primary" onClick={() => void copyLink()}>{copied ? "Copié" : "Copier"}</button>
            </span>
          </label>
          <div className="link-row">
            <a className="button" href={url} target="_blank" rel="noreferrer">Ouvrir</a>
            <button type="button" onClick={() => void refresh(screen.id, (text) => void onChanged(text), setError)}>Relancer la lecture</button>
          </div>
          <p className="meta-chip">{ip.label} · {ip.value}</p>
          <p className="field-hint">Ouvrir le lien sur cet appareil crée une nouvelle demande, avec l'adresse IP d'ici. La télé du club reste un appareil distinct.</p>
        </article>
        <article className="card settings-card stack">
          <h2>Réglages</h2>
          <label>
            Nom
            <input value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          <label>
            Emplacement
            <input value={location} onChange={(event) => setLocation(event.target.value)} placeholder="Salle cardio, accueil…" />
          </label>
          <label>
            Playlist diffusée
            <select value={playlistId} onChange={(event) => setPlaylistId(event.target.value)}>
              <option value="">Aucune — la télé reste noire</option>
              {playlists.map((playlist) => (
                <option key={playlist.id} value={playlist.id}>{playlist.name}</option>
              ))}
            </select>
          </label>
          <label className="switch-row">
            <span className="switch-copy">
              <strong>Son</strong>
              <span className="muted">Coupé par défaut. Android TV démarre plus sûrement en muet.</span>
            </span>
            <input className="switch" type="checkbox" role="switch" checked={audioEnabled} aria-label="Activer le son" onChange={(event) => setAudioEnabled(event.target.checked)} />
          </label>
          <div className="editor-foot">
            <button type="button" className="primary" disabled={!dirty} onClick={() => void save()}>{dirty ? "Enregistrer" : "Enregistré"}</button>
            <button type="button" className="danger" onClick={() => void remove()}>Supprimer</button>
          </div>
          {dirty ? <p className="field-hint">Modifications non enregistrées.</p> : null}
        </article>
        <article className="card devices-card">
          <h2>Appareils</h2>
          <p className="muted">Le code doit être celui affiché sur la télé, et l'adresse IP celle du club.</p>
          {screen.devices.length === 0 ? <p className="quiet-empty">Aucune télé n'a encore ouvert ce lien.</p> : null}
          {screen.devices.map((device) => (
            <DeviceRow
              key={device.id}
              screen={screen}
              device={device}
              onDone={onChanged}
              onError={setError}
            />
          ))}
        </article>
        <article className="card content-card">
          <h2>En cours de diffusion</h2>
          {screen.assignment && screen.assignment.items.length > 0 ? (
            <ol className="compact-list">
              {screen.assignment.items.map((item) => (
                <li key={`${item.position}-${item.mediaId}`}>
                  <span className="order-index">{item.position + 1}</span>
                  <span>{item.name}</span>
                  <em>{durationLabel(item.durationSeconds, item.type)}</em>
                </li>
              ))}
            </ol>
          ) : (
            <p className="quiet-empty">Rien n'est assigné. Choisissez une playlist dans les réglages, puis enregistrez.</p>
          )}
        </article>
      </div>
    </div>
  );
}

function CreateScreen({ onCreated }: { onCreated: (screen: Screen) => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [id, setId] = useState("");
  const [error, setError] = useState("");
  const close = useCallback(() => setOpen(false), []);

  async function create() {
    setError("");
    try {
      const result = await api<{ screen: Screen }>("/api/screens", {
        method: "POST",
        body: JSON.stringify({ name, location, id: id || undefined }),
      });
      setOpen(false);
      setName("");
      setLocation("");
      setId("");
      onCreated(result.screen);
    } catch (reason) {
      setError(message(reason));
    }
  }

  return (
    <>
      <button type="button" className="primary" onClick={() => setOpen(true)}>Nouvel écran</button>
      {open ? (
        <Dialog title="Nouvel écran" onClose={close}>
          <form className="stack" onSubmit={(event) => { event.preventDefault(); void create(); }}>
            <label>
              Nom
              <span className="field-hint">Ce que l'équipe reconnaît, par exemple Cardio.</span>
              <input value={name} onChange={(event) => setName(event.target.value)} required minLength={2} autoFocus />
            </label>
            <label>
              Emplacement
              <input value={location} onChange={(event) => setLocation(event.target.value)} placeholder="Salle cardio, accueil…" />
            </label>
            <label>
              Identifiant du lien
              <span className="field-hint">Fin de l'adresse, par exemple ecran-cardio. Laissez vide pour le générer.</span>
              <input value={id} onChange={(event) => setId(event.target.value)} placeholder="ecran-cardio" />
            </label>
            {error ? <Banner tone="error">{error}</Banner> : null}
            <div className="row-actions">
              <button className="primary" type="submit">Créer l'écran</button>
              <button type="button" onClick={close}>Annuler</button>
            </div>
          </form>
        </Dialog>
      ) : null}
    </>
  );
}

function DroppedCallout({ screens, navigate }: { screens: Screen[]; navigate: (to: string) => void }) {
  const dropped = screens.filter((screen) => screen.dropped);
  if (dropped.length === 0) return null;
  return (
    <div className="callout alert">
      <div>
        <strong>{dropped.length === 1 ? "Une télé ne répond plus." : `${dropped.length} télés ne répondent plus.`}</strong>
        <p>
          {dropped.map((screen, index) => (
            <span key={screen.id}>
              {index > 0 ? ", " : ""}
              <a href={`/ecrans/${screen.id}`} onClick={(event) => { event.preventDefault(); navigate(`/ecrans/${screen.id}`); }}>{screen.name}</a>
            </span>
          ))}
        </p>
      </div>
    </div>
  );
}

function PendingCallout({ screens, navigate }: { screens: Screen[]; navigate: (to: string) => void }) {
  const count = screens.reduce((total, screen) => total + screen.devices.filter((device) => device.status === "pending").length, 0);
  if (count === 0) return null;
  return (
    <div className="callout">
      <div>
        <strong>{count === 1 ? "1 télé attend une autorisation." : `${count} télés attendent une autorisation.`}</strong>
        <p>Le code doit correspondre à celui affiché sur la télé.</p>
      </div>
      <button type="button" className="primary" onClick={() => navigate("/autorisations")}>Voir les codes</button>
    </div>
  );
}

function ipOf(screen: Screen): { label: string; value: string } {
  if (screen.playbackIp) return { label: "IP de lecture", value: screen.playbackIp };
  const request = screen.devices.find((device) => device.ip);
  if (request?.ip) return { label: "Dernière demande", value: request.ip };
  return { label: "IP de lecture", value: "—" };
}

function readScreenFilter(): ScreenFilter {
  if (window.location.hash === "#en-lecture") return "online";
  if (window.location.hash === "#en-attente") return "waiting";
  if (window.location.hash === "#hors-ligne") return "offline";
  return "all";
}

async function refresh(id: string, setNotice: (text: string) => void, setError: (text: string) => void) {
  try {
    await api(`/api/screens/${id}/refresh`, { method: "POST" });
    setNotice("La télé relance la lecture d'ici une trentaine de secondes.");
  } catch (reason) {
    setError(message(reason));
  }
}

function message(reason: unknown): string {
  return reason instanceof Error ? reason.message : "Erreur inattendue";
}
